/**
 * Global Command Palette (Cmd+K / Ctrl+K)
 *
 * Provides quick navigation and search across the platform.
 * Inspired by VS Code, Linear, and other modern apps.
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import {
  LayoutDashboard,
  Building2,
  FileText,
  Settings,
  Import,
  Repeat,
  FileStack,
  ClipboardList,
  FileDown,
  FileBarChart,
  FlaskConical,
  Users,
  Sparkles,
  TrendingUp,
  BarChart3,
  Brain,
  Inbox,
  Network,
  Key,
  Wand2,
  Calculator,
  History,
  Search,
  Moon,
  Sun,
  Monitor,
  UserCircle,
  LogOut,
  Plug,
  Globe,
  Activity,
  Landmark,
  CircleDollarSign,
  Briefcase,
  CalendarClock,
  Scale,
  Upload,
  Receipt,
  Shield,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { trpc } from "@/lib/trpc";

// Navigation items for quick access
const navigationItems = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/dashboard",
    group: "Navigation",
    keywords: ["home", "overview"],
  },
  {
    icon: Building2,
    label: "All Transactions",
    path: "/transactions",
    group: "Navigation",
    keywords: ["list", "entries"],
  },
  {
    icon: Import,
    label: "Import Transactions",
    path: "/bank-import",
    group: "Navigation",
    keywords: ["upload", "csv", "bank"],
  },
  {
    icon: Repeat,
    label: "Reconciliation",
    path: "/reconciliation",
    group: "Navigation",
    keywords: ["match", "bank"],
  },
  {
    icon: FileStack,
    label: "Templates",
    path: "/templates",
    group: "Navigation",
    keywords: ["recurring"],
  },
  {
    icon: ClipboardList,
    label: "Tax Reports",
    path: "/tax",
    group: "Compliance",
    keywords: ["corporate", "filing"],
  },
  {
    icon: Calculator,
    label: "VAT Report",
    path: "/vat",
    group: "Compliance",
    keywords: ["mwst", "mehrwertsteuer"],
  },
  {
    icon: FileDown,
    label: "Tax Export",
    path: "/tax-export",
    group: "Compliance",
    keywords: ["download", "datev"],
  },
  {
    icon: FileBarChart,
    label: "Financial Statements",
    path: "/statements",
    group: "Compliance",
    keywords: ["balance", "income"],
  },
  {
    icon: TrendingUp,
    label: "Financial Ratios",
    path: "/ratios",
    group: "Compliance",
    keywords: ["liquidity", "leverage"],
  },
  {
    icon: FlaskConical,
    label: "R&D Projects",
    path: "/rnd-projects",
    group: "Compliance",
    keywords: ["research", "innovation"],
  },
  {
    icon: Inbox,
    label: "File Inbox",
    path: "/file-inbox",
    group: "Documents",
    keywords: ["upload", "drop"],
  },
  {
    icon: Brain,
    label: "Intelligence",
    path: "/intelligence",
    group: "Documents",
    keywords: ["ai", "analysis"],
  },
  {
    icon: Wand2,
    label: "Company Discovery",
    path: "/company-discovery",
    group: "Documents",
    keywords: ["setup", "start", "onboarding", "new company"],
  },
  {
    icon: FileText,
    label: "Documents",
    path: "/documents",
    group: "Documents",
    keywords: ["files", "invoices"],
  },
  {
    icon: Users,
    label: "Vendors",
    path: "/vendors",
    group: "Data",
    keywords: ["suppliers", "partners"],
  },
  {
    icon: Users,
    label: "Customers",
    path: "/customers",
    group: "Data",
    keywords: ["clients"],
  },
  {
    icon: Sparkles,
    label: "Categorization Rules",
    path: "/categorization-rules",
    group: "Automation",
    keywords: ["auto", "classify"],
  },
  {
    icon: TrendingUp,
    label: "Rule Suggestions",
    path: "/rule-suggestions",
    group: "Automation",
    keywords: ["ai", "recommend"],
  },
  {
    icon: BarChart3,
    label: "Categorization Analytics",
    path: "/categorization-analytics",
    group: "Analytics",
    keywords: ["stats", "metrics"],
  },
  {
    icon: History,
    label: "Import History",
    path: "/import-history",
    group: "Analytics",
    keywords: ["past", "logs"],
  },
  {
    icon: Building2,
    label: "Companies",
    path: "/companies",
    group: "Settings",
    keywords: ["organization", "manage"],
  },
  {
    icon: Network,
    label: "Company Settings",
    path: "/company-settings",
    group: "Settings",
    keywords: ["config"],
  },
  {
    icon: UserCircle,
    label: "User Profile",
    path: "/user-profile",
    group: "Settings",
    keywords: ["account", "me"],
  },
  {
    icon: Brain,
    label: "AI Settings",
    path: "/ai-settings",
    group: "Settings",
    keywords: ["llm", "model"],
  },
  {
    icon: Key,
    label: "API Keys",
    path: "/api-keys",
    group: "Settings",
    keywords: ["tokens", "integration"],
  },
  {
    icon: CircleDollarSign,
    label: "CFO Dashboard",
    path: "/cfo-dashboard",
    group: "Navigation",
    keywords: ["executive", "financial", "overview"],
  },
  {
    icon: Plug,
    label: "Integrations",
    path: "/integrations",
    group: "Settings",
    keywords: ["bexio", "xero", "connect", "sync"],
  },
  {
    icon: Landmark,
    label: "Accountant Marketplace",
    path: "/accountants",
    group: "Data",
    keywords: ["treuhander", "accountant", "marketplace"],
  },
  {
    icon: Activity,
    label: "AI Usage",
    path: "/ai-usage",
    group: "Analytics",
    keywords: ["credits", "consumption", "llm"],
  },
  {
    icon: Globe,
    label: "Historical Tax",
    path: "/historical-tax",
    group: "Compliance",
    keywords: ["comparison", "past", "years"],
  },
  {
    icon: Activity,
    label: "Monitoring",
    path: "/monitoring",
    group: "Settings",
    keywords: ["health", "status", "observability"],
  },
  {
    icon: Upload,
    label: "Bulk Import",
    path: "/bulk-import",
    group: "Documents",
    keywords: ["batch", "multiple", "mass"],
  },
  {
    icon: Briefcase,
    label: "Loan Application",
    path: "/loan-application",
    group: "Compliance",
    keywords: ["credit", "bank", "financing"],
  },
  {
    icon: Scale,
    label: "Swiss GAAP FER",
    path: "/swiss-gaap-fer",
    group: "Compliance",
    keywords: ["accounting", "standards", "gaap"],
  },
  {
    icon: CalendarClock,
    label: "Accrual Detector",
    path: "/accrual-detector",
    group: "Compliance",
    keywords: ["accruals", "deferrals", "period"],
  },
  {
    icon: Receipt,
    label: "Tax Optimization",
    path: "/tax-optimization",
    group: "Compliance",
    keywords: ["save", "reduce", "deductions"],
  },
  {
    icon: Shield,
    label: "Admin Panel",
    path: "/admin",
    group: "Settings",
    keywords: ["manage", "users", "system"],
  },
];

// Theme actions
const themeActions = [
  { icon: Sun, label: "Light Theme", action: "theme-light", group: "Theme" },
  { icon: Moon, label: "Dark Theme", action: "theme-dark", group: "Theme" },
  { icon: Sun, label: "Sakura Theme", action: "theme-sakura", group: "Theme" },
  {
    icon: Monitor,
    label: "Andes Theme",
    action: "theme-andes",
    group: "Theme",
  },
];

interface CommandPaletteProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CommandPalette({
  open: controlledOpen,
  onOpenChange,
}: CommandPaletteProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [, setLocation] = useLocation();
  const { theme, setTheme } = useTheme();

  // Use controlled or uncontrolled state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  // Fetch companies for quick switching
  const { data: companies = [] } = trpc.company.list.useQuery(undefined, {
    enabled: isOpen,
  });

  // Global keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(!isOpen);
      }

      // Escape to close
      if (e.key === "Escape" && isOpen) {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, setOpen]);

  const runCommand = useCallback(
    (command: () => void) => {
      setOpen(false);
      command();
    },
    [setOpen]
  );

  const handleNavigation = useCallback(
    (path: string) => {
      runCommand(() => setLocation(path));
    },
    [runCommand, setLocation]
  );

  const handleThemeChange = useCallback(
    (action: string) => {
      const themeMap: Record<string, "light" | "dark" | "sakura" | "andes"> = {
        "theme-light": "light",
        "theme-dark": "dark",
        "theme-sakura": "sakura",
        "theme-andes": "andes",
      };
      runCommand(() => setTheme(themeMap[action] || "light"));
    },
    [runCommand, setTheme]
  );

  // Group navigation items by category
  const groupedItems = navigationItems.reduce(
    (acc, item) => {
      if (!acc[item.group]) acc[item.group] = [];
      acc[item.group].push(item);
      return acc;
    },
    {} as Record<string, typeof navigationItems>
  );

  return (
    <CommandDialog open={isOpen} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Navigation Groups */}
        {Object.entries(groupedItems).map(([group, items]) => (
          <CommandGroup key={group} heading={group}>
            {items.map(item => (
              <CommandItem
                key={item.path}
                value={`${item.label} ${item.keywords?.join(" ") || ""}`}
                onSelect={() => handleNavigation(item.path)}
              >
                <item.icon className="mr-2 h-4 w-4" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}

        <CommandSeparator />

        {/* Theme Actions */}
        <CommandGroup heading="Theme">
          {themeActions.map(action => (
            <CommandItem
              key={action.action}
              value={action.label}
              onSelect={() => handleThemeChange(action.action)}
            >
              <action.icon className="mr-2 h-4 w-4" />
              <span>{action.label}</span>
              {theme === action.action.replace("theme-", "") && (
                <CommandShortcut>Active</CommandShortcut>
              )}
            </CommandItem>
          ))}
        </CommandGroup>

        {/* Company Quick Switch */}
        {companies.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Switch Company">
              {companies.slice(0, 5).map(company => (
                <CommandItem
                  key={company.id}
                  value={`company ${company.name}`}
                  onSelect={() => handleNavigation(`/companies`)}
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  <span>{company.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />

        {/* Quick Actions */}
        <CommandGroup heading="Quick Actions">
          <CommandItem
            value="new transaction"
            onSelect={() => handleNavigation("/transactions")}
          >
            <FileText className="mr-2 h-4 w-4" />
            <span>New Transaction</span>
            <CommandShortcut>N</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="import bank statement"
            onSelect={() => handleNavigation("/bank-import")}
          >
            <Import className="mr-2 h-4 w-4" />
            <span>Import Bank Statement</span>
            <CommandShortcut>I</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="generate report"
            onSelect={() => handleNavigation("/statements")}
          >
            <FileBarChart className="mr-2 h-4 w-4" />
            <span>Generate Report</span>
            <CommandShortcut>R</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

/**
 * Keyboard shortcut indicator for the UI
 * Shows Cmd+K on Mac, Ctrl+K on Windows/Linux
 */
export function CommandPaletteTrigger() {
  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    setIsMac(navigator.platform.toUpperCase().includes("MAC"));
  }, []);

  return (
    <span className="text-xs text-muted-foreground">
      {isMac ? "⌘K" : "Ctrl+K"}
    </span>
  );
}

export default CommandPalette;
